// src/pages/PLCDetail.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SetValueModal from "../components/SetValueModal";
import api from "../api";
import { getVariableGroups } from "../api";

import { useThemeMode } from "../theme/ThemeContext";

import {
  Paper, Typography, Box, Button, Chip, Grid,
  IconButton, Stack,
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, TimeScale, Tooltip, Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Tooltip, Legend);

const crosshairPlugin = {
  id: "crosshair",
  afterDraw(chart) {
    if (!chart.tooltip?._active?.length) return;
    const { ctx, chartArea: { top, bottom } } = chart;
    const x = chart.tooltip._active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(120,120,120,0.55)";
    ctx.stroke();
    ctx.restore();
  },
};
ChartJS.register(crosshairPlugin);

// ── Constants ─────────────────────────────────────────────────────────────────

const TIME_RANGE_OPTIONS = [
  { label: "3H",  hours: 3  },
  { label: "6H",  hours: 6  },
  { label: "12H", hours: 12 },
  { label: "24H", hours: 24 },
];

const LIVE_LATEST_MS  = 30_000;
const LIVE_HISTORY_MS = 120_000;

// Per-series colors for merged charts
const SERIES_COLORS = {
  light: [
    "rgba(37,99,235,0.95)",
    "rgba(220,38,38,0.95)",
    "rgba(22,163,74,0.95)",
    "rgba(217,119,6,0.95)",
    "rgba(147,51,234,0.95)",
    "rgba(2,132,199,0.95)",
  ],
  dark: [
    "rgba(147,197,253,1)",
    "rgba(252,165,165,1)",
    "rgba(134,239,172,1)",
    "rgba(253,230,138,1)",
    "rgba(216,180,254,1)",
    "rgba(125,211,252,1)",
  ],
};

// ── Pure chart options builder ────────────────────────────────────────────────

function buildChartOptions(mode, hours, anchorMs, showLegend = false, formatYValue = null, tz = null) {
  const isDark     = mode === "dark";
  const tickColor  = isDark ? "rgba(255,255,255,0.72)" : "#5f6b7a";
  const gridColor  = isDark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.14)";
  const tooltipBg  = isDark ? "rgba(17,24,39,0.96)"   : "rgba(255,255,255,0.96)";
  const tooltipTxt = isDark ? "#f3f4f6"               : "#111827";
  const borderClr  = isDark ? "rgba(255,255,255,0.14)": "rgba(15,23,42,0.12)";

  const endMs    = anchorMs != null ? anchorMs : Date.now();
  const xMin     = endMs - hours * 3_600_000;
  const unit     = hours <= 6 ? "minute" : "hour";
  const stepSize = hours <= 3 ? 30 : hours <= 6 ? 60 : hours <= 12 ? 1 : 2;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: showLegend, position: "top", labels: { color: tickColor, boxWidth: 12, padding: 16 } },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tooltipTxt,
        bodyColor: tooltipTxt,
        borderColor: borderClr,
        borderWidth: 1,
        callbacks: {
          title: (items) => new Date(items[0].parsed.x).toLocaleString(undefined, {
            hour: "2-digit", minute: "2-digit", day: "numeric", month: "short",
            ...(tz ? { timeZone: tz } : {}),
          }),
          label: (ctx) => {
              const y = ctx.parsed.y;
              const formatted = formatYValue ? formatYValue(ctx.dataset.label, y) : y;
              return `${ctx.dataset.label}: ${formatted}`;
            },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        min: xMin,
        max: endMs,
        time: { unit, stepSize, displayFormats: { minute: "HH:mm", hour: "HH:mm" } },
        ticks: { color: tickColor, maxTicksLimit: 8, source: "auto" },
        grid:  { color: gridColor, lineWidth: 1 },
        border: { color: gridColor },
      },
      y: {
        ticks:  { color: tickColor },
        grid:   { color: gridColor, lineWidth: 1 },
        border: { color: gridColor },
      },
    },
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(d) { return d.toISOString().slice(0, 10); }

function formatWindowLabel(hours, anchorMs, tz) {
  const endMs   = anchorMs != null ? anchorMs : Date.now();
  const startMs = endMs - hours * 3_600_000;
  const start   = new Date(startMs);
  const end     = new Date(endMs);
  const tzOpts  = tz ? { timeZone: tz } : {};
  const fmt     = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", ...tzOpts });
  const fmtDate = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...tzOpts });
  const sameDay = start.toLocaleDateString(undefined, { timeZone: tz || undefined }) ===
                  end.toLocaleDateString(undefined,   { timeZone: tz || undefined });
  if (sameDay) return `${fmtDate(start)} · ${fmt(start)} – ${fmt(end)}`;
  return `${fmtDate(start)} ${fmt(start)} – ${fmtDate(end)} ${fmt(end)}`;
}

function formatTimestamp(ts, tz) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    timeZone: tz || undefined,
  });
}

function prettyLabel(variable) {
  if (!variable) return "";
  return variable
    .replace(/^AIDI\./, "")
    .replaceAll("_", " ")
    .replace(/\bVal\b/g, "Value")
    .replace(/\bTemp\b/g, "Temp")
    .replace(/\bPress\b/g, "Pressure")
    .replace(/\bOffs\b/g, "Offset")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return String(value);
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.00$/, "");
}

function formatRange(meta) {
  if (!meta) return null;
  const hasMin = meta.min_value !== null && meta.min_value !== undefined;
  const hasMax = meta.max_value !== null && meta.max_value !== undefined;
  if (!hasMin && !hasMax) return null;
  if (hasMin && hasMax) return `${meta.min_value} to ${meta.max_value}`;
  if (hasMin) return `≥ ${meta.min_value}`;
  return `≤ ${meta.max_value}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return null;
  const secs = Math.floor((Date.now() - ts) / 1000);
  if (secs < 10) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min. ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function LiveDot({ lastFetch, offline }) {
  // Re-render every 10 s so the "ago" text stays fresh
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  if (offline) {
    return (
      <Box component="span" sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75,
        px: 1.25, py: 0.4, borderRadius: 999,
        background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.35)",
        fontSize: "0.72rem", fontWeight: 700, color: "#dc2626", letterSpacing: "0.04em",
      }}>
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: "#ef4444", flexShrink: 0 }} />
        Offline
      </Box>
    );
  }

  const ago = timeAgo(lastFetch);
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      px: 1.25, py: 0.4, borderRadius: 999,
      background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
      fontSize: "0.72rem", fontWeight: 700, color: "#16a34a", letterSpacing: "0.04em",
    }}>
      <Box component="span" sx={{
        width: 7, height: 7, borderRadius: "50%", background: "#22c55e", flexShrink: 0,
        "@keyframes livePulse": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.4, transform: "scale(0.75)" } },
        animation: "livePulse 1.8s ease-in-out infinite",
      }} />
      Live{ago ? ` · ${ago}` : ""}
    </Box>
  );
}

function TimeRangeSegmented({ value, onChange }) {
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "2px",
      p: "3px", borderRadius: 999,
      border: "1px solid var(--border-color)", background: "var(--bg-soft)",
    }}>
      {TIME_RANGE_OPTIONS.map((opt) => {
        const active = value === opt.hours;
        return (
          <Box
            key={opt.label}
            component="button"
            type="button"
            onClick={() => onChange(opt.hours)}
            sx={{
              border: 0, cursor: "pointer", px: 1.5, py: 0.6, borderRadius: 999,
              fontFamily: "var(--font-body)", fontSize: "0.74rem", fontWeight: 700,
              letterSpacing: "0.02em",
              color: active ? "var(--accent-contrast)" : "var(--text-muted)",
              background: active ? "var(--accent)" : "transparent",
              transition: "background-color 0.18s ease, color 0.18s ease",
              "&:hover": { color: active ? "var(--accent-contrast)" : "var(--text-main)" },
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}

function NoDataBox() {
  return (
    <Box sx={{
      height: "100%", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      border: "1px dashed var(--border-color)", borderRadius: "var(--card-radius-sm)",
      backgroundColor: "var(--bg-soft)", px: 2, textAlign: "center",
    }}>
      <TimelineIcon sx={{ mb: 1, color: "var(--text-muted)" }} />
      <Typography sx={{ fontWeight: 600, mb: 0.5, color: "var(--text-main)" }}>No data for this time range</Typography>
      <Typography variant="body2" sx={{ color: "var(--text-muted)" }}>Try a wider range or wait for new readings.</Typography>
    </Box>
  );
}

function ReadingsTable({ vars, latest, getVarLabel, formatVarValue, writableVars, plcTimezone }) {
  const cellBase = {
    px: 2, py: 1.25, textAlign: "left", verticalAlign: "middle",
    borderBottom: "1px solid var(--border-color)", fontSize: "0.86rem",
  };
  const headCell = {
    px: 2, py: 1.25, textAlign: "left",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-muted)", fontWeight: 700, fontSize: "0.66rem",
    letterSpacing: "0.07em", textTransform: "uppercase",
  };

  return (
    <Paper elevation={0} sx={{
      mb: 2.5, borderRadius: "var(--card-radius)",
      border: "1px solid var(--border-color)", backgroundColor: "var(--bg-elevated)",
      boxShadow: "var(--shadow-soft)", overflow: "hidden",
    }}>
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid var(--border-color)" }}>
        <Typography variant="overline" sx={{
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", color: "var(--text-muted)",
        }}>
          Latest Values
        </Typography>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...headCell, width: 0 }} />
              <Box component="th" sx={headCell}>Variable</Box>
              <Box component="th" sx={{ ...headCell, textAlign: "right" }}>Value</Box>
              <Box component="th" sx={{ ...headCell, textAlign: "right" }}>Last updated</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {vars.map((meta) => {
              const v    = meta.variable_name;
              const val  = latest[v]?.value;
              const ts   = latest[v]?.timestamp;
              const wm   = writableVars.find(w => w.variable_name === v);
              const tsMs = ts ? new Date(ts).getTime() : null;
              const fresh = tsMs != null && (Date.now() - tsMs) < 3 * 60 * 1000;
              return (
                <Box component="tr" key={v} sx={{
                  "&:last-of-type td": { borderBottom: "none" },
                  "&:hover td": { backgroundColor: "var(--bg-soft)" },
                }}>
                  <Box component="td" sx={{ ...cellBase, pr: 0 }}>
                    <Box component="span" title={fresh ? "Fresh reading" : "Stale reading"} sx={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: fresh ? "#22c55e" : "rgba(148,163,184,0.7)",
                    }} />
                  </Box>
                  <Box component="td" sx={{ ...cellBase, color: "var(--text-main)", fontWeight: 600 }}>
                    {getVarLabel(v)}
                  </Box>
                  <Box component="td" sx={{
                    ...cellBase, textAlign: "right", color: "var(--text-main)",
                    fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                  }}>
                    {formatVarValue(v, val)}
                    {wm?.unit && (
                      <Box component="span" sx={{ ml: 0.4, fontSize: "0.78rem", fontWeight: 500, color: "var(--text-muted)" }}>
                        {wm.unit}
                      </Box>
                    )}
                  </Box>
                  <Box component="td" sx={{
                    ...cellBase, textAlign: "right", color: "var(--text-muted)",
                    fontSize: "0.78rem", whiteSpace: "nowrap",
                  }}>
                    {formatTimestamp(ts, plcTimezone)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function ExpandedModal({ varKey, label, displayHistory, latestValue, latestTimestamp, mode, globalHours, anchorMs, onStepBack, onStepForward, onGoLive, onClose, isGroup, groupDatasets, formatYValue, plcTimezone }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const opts = buildChartOptions(mode, globalHours, anchorMs, isGroup, formatYValue, plcTimezone);
  const canGoForward = anchorMs != null;

  return (
    <Box onClick={onClose} sx={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
      zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center",
      p: { xs: 1, md: 3 },
    }}>
      <Paper onClick={(e) => e.stopPropagation()} elevation={0} sx={{
        width: "100%", maxWidth: 1200, height: "92dvh",
        display: "flex", flexDirection: "column",
        borderRadius: "var(--card-radius)", border: "1px solid var(--border-color)",
        backgroundColor: "var(--bg-elevated)", overflow: "hidden",
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: "1px solid var(--border-color)", flexShrink: 0 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-main)", lineHeight: 1.2 }}>{label}</Typography>
            {!isGroup && (
              <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                {latestValue} · {formatWindowLabel(globalHours, anchorMs, plcTimezone)}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={onStepBack} title="Step back"><ChevronLeftIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={onStepForward} disabled={!canGoForward} title="Step forward"><ChevronRightIcon fontSize="small" /></IconButton>
            {anchorMs != null && (
              <Button size="small" variant="outlined" onClick={onGoLive}
                sx={{ fontSize: "0.72rem", px: 1.5, py: 0.25, minWidth: 0 }}>
                Live
              </Button>
            )}
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, p: 3 }}>
          {(isGroup ? groupDatasets.some(d => d.data.length > 0) : displayHistory.length > 0) ? (
            <Line
              key={`expanded-${varKey}-${globalHours}-${anchorMs}`}
              options={opts}
              data={{ datasets: isGroup ? groupDatasets : [{
                label, data: displayHistory, tension: 0.25, pointRadius: 0, pointHoverRadius: 0,
                borderColor: SERIES_COLORS[mode]?.[0] ?? "rgba(37,99,235,0.95)", borderWidth: 2.5,
              }] }}
            />
          ) : <NoDataBox />}
        </Box>
      </Paper>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function PLCDetail() {
  const { mode } = useThemeMode();
  const { plcId } = useParams();
  const navigate  = useNavigate();

  const [plcName, setPlcName]           = useState("");
  const [plcTimezone, setPlcTimezone]   = useState("Asia/Kolkata");
  const [latest, setLatest]             = useState({});
  const [histories, setHistories]       = useState({});
  const [selectedVars, setSelectedVars] = useState([]);
  const [importantVars, setImportantVars] = useState([]);
  const [writableVars, setWritableVars] = useState([]);
  const [variableGroups, setVariableGroups] = useState([]);
  const [globalHours, setGlobalHours]   = useState(24);
  const [anchorMs, setAnchorMs]         = useState(null); // null = live/now
  const [varOrder, setVarOrder]         = useState([]);
  const [lastFetch, setLastFetch]       = useState(null); // timestamp of last successful /latest poll
  const [plcOffline, setPlcOffline]     = useState(false);
  const [expandedVar, setExpandedVar]   = useState(null);
  const [ctrlState, setCtrlState]       = useState({ open: false, variable: null, meta: null });

  const selectedVarsRef  = useRef([]);
  const globalHoursRef   = useRef(24);
  const anchorMsRef      = useRef(null);
  const allVarNamesRef   = useRef([]);
  const dragSrcRef       = useRef(null);
  const dragOverRef      = useRef(null);
  useEffect(() => { selectedVarsRef.current = selectedVars; },  [selectedVars]);
  useEffect(() => { globalHoursRef.current  = globalHours; },   [globalHours]);
  useEffect(() => { anchorMsRef.current     = anchorMs; },      [anchorMs]);
  useEffect(() => {
    const names = new Set();
    // Only track history for vars that have plots
    importantVars
      .filter(v => typeof v !== "object" || v.show_plot !== false)
      .forEach(v => names.add(typeof v === "object" ? v.variable_name : v));
    variableGroups.forEach(g => g.variable_names.forEach(v => names.add(v)));
    allVarNamesRef.current = [...names];
  }, [importantVars, variableGroups]);

  const getWritableMetaForDisplayVar = useCallback((v) =>
    writableVars.find((w) => w.display_name === v || w.variable_name === v), [writableVars]);

  // ── Fetch history for a variable ──────────────────────────────────────────
  const fetchHistoryRange = useCallback(async (variable, hours, anchor) => {
    const endMs   = anchor != null ? anchor : Date.now();
    const startMs = endMs - hours * 3_600_000;
    try {
      const data = await api.get(
        `/plc/${plcId}/readings/${encodeURIComponent(variable)}?start=${new Date(startMs).toISOString()}&end=${new Date(endMs).toISOString()}`
      );
      const points = (data.readings || []).map((r) => ({
        x: new Date(r.timestamp),
        y: typeof r.value === "number" ? r.value : parseFloat(r.value),
      }));
      setHistories((prev) => {
        const existing = prev[variable] || [];
        if (existing.length === 0) {
          return { ...prev, [variable]: points };
        }
        // Merge new points with existing — never shrink the cache
        const byTime = new Map(existing.map((p) => [p.x.getTime(), p]));
        for (const p of points) byTime.set(p.x.getTime(), p);
        const sorted = [...byTime.values()].sort((a, b) => a.x - b.x);
        // In live mode, trim to 26H to prevent unbounded memory growth
        if (anchor == null) {
          const cutoff = Date.now() - 26 * 3_600_000;
          return { ...prev, [variable]: sorted.filter((p) => p.x.getTime() >= cutoff) };
        }
        return { ...prev, [variable]: sorted };
      });
    } catch (err) {
      console.error("History fetch error", variable, err);
      // Don't clear existing cache on error
    }
  }, [plcId]);

  const fetchAllHistories = useCallback((hours, anchor) => {
    allVarNamesRef.current.forEach(v => fetchHistoryRange(v, hours, anchor));
  }, [fetchHistoryRange]);

  const getDisplayHistory = useCallback((varKey) => {
    const endMs   = anchorMs != null ? anchorMs : Date.now();
    const startMs = endMs - globalHours * 3_600_000;
    return (histories[varKey] || []).filter((p) => {
      const t = p.x.getTime();
      return t >= startMs && t <= endMs;
    });
  }, [histories, anchorMs, globalHours]);

  // ── Time navigation ────────────────────────────────────────────────────────
  const handleGlobalHoursChange = useCallback((hours) => {
    const prevHours = globalHoursRef.current;
    setGlobalHours(hours);
    // Only fetch when expanding the window or in historical mode — smaller windows
    // are already covered by the cached data (initial load fetches 24H).
    if (hours > prevHours || anchorMsRef.current != null) {
      fetchAllHistories(hours, anchorMsRef.current);
    }
  }, [fetchAllHistories]);

  const stepBack = useCallback(() => {
    const base      = anchorMsRef.current != null ? anchorMsRef.current : Date.now();
    const newAnchor = base - globalHoursRef.current * 3_600_000;
    setAnchorMs(newAnchor);
    fetchAllHistories(globalHoursRef.current, newAnchor);
  }, [fetchAllHistories]);

  const stepForward = useCallback(() => {
    if (anchorMsRef.current == null) return;
    const next = anchorMsRef.current + globalHoursRef.current * 3_600_000;
    if (next >= Date.now()) {
      setAnchorMs(null);
      fetchAllHistories(globalHoursRef.current, null);
    } else {
      setAnchorMs(next);
      fetchAllHistories(globalHoursRef.current, next);
    }
  }, [fetchAllHistories]);

  const goLive = useCallback(() => {
    setAnchorMs(null);
    fetchAllHistories(globalHoursRef.current, null);
  }, [fetchAllHistories]);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!plcId) return;
    (async () => {
      try {
        const [infoRes, importantRes, latestRes, writableRes, groupsRes] = await Promise.all([
          api.get(`/plc/${plcId}`),
          api.get(`/plc/${plcId}/important_vars`),
          api.get(`/plc/${plcId}/latest`),
          api.get(`/plc/${plcId}/writable_vars`),
          getVariableGroups(plcId),
        ]);

        const important = importantRes.important_vars || [];
        const latestJson = latestRes || {};
        const writable   = writableRes.writable_vars || [];
        const groups     = groupsRes.groups || [];

        setImportantVars(important);
        setLatest(latestJson);
        const initTimestamps = Object.values(latestJson)
          .map(dp => dp?.timestamp ? new Date(dp.timestamp).getTime() : 0)
          .filter(Boolean);
        if (initTimestamps.length > 0) {
          const initMaxTs = Math.max(...initTimestamps);
          const initStale = (Date.now() - initMaxTs) > 3 * 60 * 1000;
          setPlcOffline(initStale);
          if (!initStale) setLastFetch(Date.now());
        }
        setWritableVars(writable);
        setPlcName(infoRes.name || `PLC ${plcId}`);
        setPlcTimezone(infoRes.timezone || "Asia/Kolkata");
        setVariableGroups(groups);

        // Separate plot vars from display-only vars
        const plotVarKeys = important.length > 0
          ? important.filter(v => typeof v !== "object" || v.show_plot !== false).map(v => v.variable_name)
          : Object.keys(latestJson || {});
        const groupKeys = groups.map((_, i) => `group-${i}`);
        const allKeys = [...groupKeys, ...plotVarKeys];
        setSelectedVars(allKeys);
        setVarOrder(allKeys);

        // Only fetch history for plot vars + group vars (display-only vars use /latest)
        const allVarsToFetch = new Set(plotVarKeys);
        groups.forEach(g => g.variable_names.forEach(v => allVarsToFetch.add(v)));
        allVarsToFetch.forEach((v) => fetchHistoryRange(v, 24, null));
      } catch (err) {
        console.error(err);
      }
    })();
  }, [plcId, fetchHistoryRange]);

  // ── Live polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!plcId) return;

    const latestTimer = setInterval(async () => {
      try {
        const res = await api.get(`/plc/${plcId}/latest`);
        const newLatest = res || {};

        // Always update online/offline — even when viewing past history
        const timestamps = Object.values(newLatest)
          .map(dp => dp?.timestamp ? new Date(dp.timestamp).getTime() : 0)
          .filter(Boolean);
        if (timestamps.length > 0) {
          // Only determine staleness when there is data — if no vars configured, don't flip to offline
          const maxTs = Math.max(...timestamps);
          const stale = (Date.now() - maxTs) > 3 * 60 * 1000;
          setPlcOffline(stale);
          if (!stale) setLastFetch(Date.now());
        }

        // Only update display values and live history when in live mode
        if (anchorMsRef.current == null) {
          setLatest(newLatest);
          setHistories((prev) => {
            const updated = { ...prev };
            for (const [varKey, dp] of Object.entries(newLatest)) {
              if (dp?.timestamp == null || dp?.value == null) continue;
              const existing = prev[varKey] || [];
              const newPt = { x: new Date(dp.timestamp), y: typeof dp.value === "number" ? dp.value : parseFloat(dp.value) };
              const lastPt = existing[existing.length - 1];
              if (!lastPt || newPt.x > lastPt.x) {
                updated[varKey] = [...existing, newPt];
              }
            }
            return updated;
          });
        }
      } catch (e) {
        console.error("Live latest error", e);
        setPlcOffline(true);
      }
    }, LIVE_LATEST_MS);

    const historyTimer = setInterval(() => {
      if (anchorMsRef.current != null) return; // don't refresh history when viewing past
      allVarNamesRef.current.forEach(v => fetchHistoryRange(v, globalHoursRef.current, null));
    }, LIVE_HISTORY_MS);

    return () => { clearInterval(latestTimer); clearInterval(historyTimer); };
  }, [plcId, fetchHistoryRange]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const importantVarsMeta = useMemo(() => {
    const m = {};
    for (const iv of importantVars) {
      if (typeof iv === "object") m[iv.variable_name] = iv;
    }
    return m;
  }, [importantVars]);

  const getVarLabel = useCallback((varKey) =>
    importantVarsMeta[varKey]?.display_name || prettyLabel(varKey), [importantVarsMeta]);

  const formatVarValue = useCallback((varKey, value) => {
    const meta = importantVarsMeta[varKey];
    if (meta?.is_boolean) {
      if (value === null || value === undefined) return "—";
      return Number(value) === 0 ? (meta.bool_false_label || "Off") : (meta.bool_true_label || "On");
    }
    return formatValue(value);
  }, [importantVarsMeta]);

  const toggleVar = (v) => {
    setSelectedVars((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
    if (!histories[v]) fetchHistoryRange(v, globalHours, anchorMs);
  };

  const handleControlConfirm = async (variable, value) => {
    await api.post(`/plc/${plcId}/control`, { variable, value });
    const refreshed = await api.get(`/plc/${plcId}/latest`);
    setLatest(refreshed);
    setCtrlState({ open: false, variable: null, meta: null });
  };

  // Vars shown in the Latest Values table. Backward-compat: a var with an
  // explicit show_table wins; otherwise legacy display-only vars (show_plot===false)
  // are surfaced in the table.
  const tableVars = useMemo(() =>
    importantVars.filter(v =>
      typeof v === "object" &&
      (v.show_table === true || (v.show_table === undefined && v.show_plot === false))
    ),
    [importantVars]
  );

  // All available vars in the user's drag-defined order — plot vars only
  const allVars = importantVars.length > 0
    ? importantVars
        .filter(v => typeof v !== "object" || v.show_plot !== false)
        .map((v) => (typeof v === "object" ? v.variable_name : v))
    : Object.keys(latest || {});

  // Vars that appear in at least one merged group — excluded from individual cards/charts
  const groupedVarSet = useMemo(
    () => new Set(variableGroups.flatMap(g => g.variable_names)),
    [variableGroups]
  );

  // Solo vars only (not in any group)
  const soloVars = useMemo(
    () => allVars.filter(v => !groupedVarSet.has(v)),
    [allVars, groupedVarSet]
  );

  // varOrder is the canonical order for solo vars; if a new var appears, append
  const displayVars = varOrder.length > 0
    ? [...varOrder.filter(v => soloVars.includes(v)), ...soloVars.filter(v => !varOrder.includes(v))]
    : soloVars;

  const colors = SERIES_COLORS[mode] ?? SERIES_COLORS.light;

  // ── Drag-and-drop handlers ─────────────────────────────────────────────────
  function onDragStart(v) {
    dragSrcRef.current = v;
  }

  function onDragEnter(v) {
    dragOverRef.current = v;
  }

  function onDragEnd() {
    const src  = dragSrcRef.current;
    const over = dragOverRef.current;
    if (!src || !over || src === over) { dragSrcRef.current = null; dragOverRef.current = null; return; }
    setVarOrder(prev => {
      const order = prev.length > 0 ? [...prev] : [...displayVars];
      const si = order.indexOf(src);
      const oi = order.indexOf(over);
      if (si === -1 || oi === -1) return prev;
      order.splice(si, 1);
      order.splice(oi, 0, src);
      return order;
    });
    // Also reorder selectedVars to keep charts in sync
    setSelectedVars(prev => {
      const si = prev.indexOf(src);
      if (si === -1) return prev;
      const next = [...prev];
      next.splice(si, 1);
      const targetIdx = next.indexOf(over);
      next.splice(targetIdx === -1 ? next.length : targetIdx, 0, src);
      return next;
    });
    dragSrcRef.current = null;
    dragOverRef.current = null;
  }

  // ── Expanded modal state helpers ──────────────────────────────────────────
  const expandedIsGroup  = expandedVar?.startsWith?.("group-");
  const expandedGroupIdx = expandedIsGroup ? parseInt(expandedVar.split("-")[1], 10) : null;
  const expandedGroup    = expandedIsGroup ? variableGroups[expandedGroupIdx] : null;

  const expandedGroupDatasets = expandedGroup
    ? expandedGroup.variable_names.map((v, i) => ({
        label: getVarLabel(v),
        data: getDisplayHistory(v),
        tension: 0.25, pointRadius: 0, pointHoverRadius: 0,
        borderColor: colors[i % colors.length],
        borderWidth: 2.5,
      }))
    : [];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <Navbar />

      <Box sx={{
        px: { xs: 2, md: 3 }, pt: 12, pb: 6, minHeight: "70vh",
        background: "radial-gradient(circle at top left, var(--accent-glow), transparent 32%), var(--bg-main)",
      }}>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Page heading */}
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2, mt: 0.5 }}>
          <IconButton
            onClick={() => navigate("/plc")}
            size="small"
            title="Back to PLCs"
            sx={{
              border: "1px solid var(--border-color)", background: "var(--bg-elevated)",
              color: "var(--text-muted)",
              "&:hover": { color: "var(--accent)", borderColor: "var(--accent-border)", background: "var(--accent-soft)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h4" sx={{
            fontWeight: 700, color: "var(--text-main)", lineHeight: 1.15,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}>
            {plcName}
          </Typography>
          {anchorMs == null && <LiveDot lastFetch={lastFetch} offline={plcOffline} />}
        </Stack>

        {/* Sticky time toolbar */}
        <Box sx={{
          position: "sticky", top: "calc(var(--navbar-height, 76px) + 10px)", zIndex: 40,
          mb: 3, px: { xs: 1.5, md: 2 }, py: 1,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 1,
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--nav-border, var(--border-color))",
          background: "var(--nav-bg, var(--bg-elevated))",
          backdropFilter: "blur(16px) saturate(160%)",
          WebkitBackdropFilter: "blur(16px) saturate(160%)",
          boxShadow: "var(--shadow-soft)",
        }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={stepBack} title="Step back one window"
              sx={{ color: "var(--text-main)" }}>
              <ChevronLeftIcon />
            </IconButton>
            <TimeRangeSegmented value={globalHours} onChange={handleGlobalHoursChange} />
            <IconButton size="small" onClick={stepForward} disabled={anchorMs == null} title="Step forward one window"
              sx={{ color: "var(--text-main)" }}>
              <ChevronRightIcon />
            </IconButton>
            {anchorMs != null && (
              <Button size="small" variant="outlined" onClick={goLive}
                sx={{ fontSize: "0.75rem", px: 1.5, py: 0.25, minWidth: 0, ml: 0.5 }}>
                Live
              </Button>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
            {formatWindowLabel(globalHours, anchorMs, plcTimezone)}
          </Typography>
        </Box>

        {/* Variable cards — group cards + solo var cards, drag to reorder */}
        <Typography variant="overline" sx={{
          display: "block", mb: 1, fontSize: "0.66rem", fontWeight: 700,
          letterSpacing: "0.1em", color: "var(--text-muted)",
        }}>
          Variables · click to toggle charts · drag to reorder
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
          {/* Unified card list — groups and solo vars interleaved in drag order */}
          {varOrder.filter(key => {
            if (key.startsWith("group-")) {
              const gi = parseInt(key.split("-")[1], 10);
              return !!variableGroups[gi];
            }
            return soloVars.includes(key);
          }).map((key) => {
            const isGroup = key.startsWith("group-");
            const selected = selectedVars.includes(key);

            if (isGroup) {
              const gi    = parseInt(key.split("-")[1], 10);
              const group = variableGroups[gi];
              return (
                <Paper
                  key={key}
                  elevation={0}
                  draggable
                  onDragStart={() => onDragStart(key)}
                  onDragEnter={() => onDragEnter(key)}
                  onDragEnd={onDragEnd}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => toggleVar(key)}
                  sx={{
                    p: 1.5, borderRadius: "var(--card-radius-sm)",
                    border: selected ? "1.5px solid var(--accent)" : "1px solid var(--border-color)",
                    backgroundColor: selected ? ("var(--accent-soft)") : "var(--bg-elevated)",
                    cursor: "grab",
                    transition: "border-color 0.15s, background-color 0.15s",
                    userSelect: "none",
                    "&:hover": { borderColor: "var(--accent)", boxShadow: "0 0 0 3px var(--accent-ring)" },
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                    <Typography variant="caption" sx={{
                      color: selected ? "var(--accent)" : "var(--text-muted)",
                      display: "block", mb: 0.75, fontWeight: 600,
                    }}>
                      {group.name || `Group ${gi + 1}`}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {group.variable_names.map((v, i) => {
                        const wm = getWritableMetaForDisplayVar(v);
                        return (
                          <Box key={v} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                            <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: colors[i % colors.length], flexShrink: 0, mb: "1px" }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: selected ? "var(--accent)" : "var(--text-muted)", display: "block", lineHeight: 1.2 }}>
                                {getVarLabel(v)}
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: selected ? ("var(--accent-strong)") : "var(--text-main)", lineHeight: 1.2 }}>
                                {formatVarValue(v, latest[v]?.value)}
                                {wm?.unit && <Box component="span" sx={{ ml: 0.4, fontSize: "0.72rem", fontWeight: 500, color: "var(--text-muted)" }}>{wm.unit}</Box>}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                </Paper>
              );
            }

            // Solo var card
            const v  = key;
            const wm = getWritableMetaForDisplayVar(v);
            return (
              <Paper
                key={key}
                elevation={0}
                draggable
                onDragStart={() => onDragStart(v)}
                onDragEnter={() => onDragEnter(v)}
                onDragEnd={onDragEnd}
                onDragOver={e => e.preventDefault()}
                onClick={() => toggleVar(v)}
                sx={{
                  p: 1.5,
                  borderRadius: "var(--card-radius-sm)",
                  border: selected ? "1.5px solid var(--accent)" : "1px solid var(--border-color)",
                  backgroundColor: selected ? ("var(--accent-soft)") : "var(--bg-elevated)",
                  cursor: "grab",
                  transition: "border-color 0.15s, background-color 0.15s",
                  userSelect: "none",
                  width: "fit-content",
                  minWidth: 80,
                  "&:hover": { borderColor: "var(--accent)", boxShadow: "0 0 0 3px var(--accent-ring)" },
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <Typography variant="caption" sx={{
                  color: selected ? "var(--accent)" : "var(--text-muted)",
                  display: "block", mb: 0.25, fontWeight: selected ? 700 : 400,
                  whiteSpace: "nowrap",
                }}>
                  {getVarLabel(v)}
                  {wm && <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>✎</Box>}
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: selected ? ("var(--accent-strong)") : "var(--text-main)",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}>
                  {formatVarValue(v, latest[v]?.value)}
                  {wm?.unit && <Box component="span" sx={{ ml: 0.5, fontSize: "0.75rem", fontWeight: 500, color: "var(--text-muted)" }}>{wm.unit}</Box>}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <SetValueModal
          open={ctrlState.open} variable={ctrlState.variable} meta={ctrlState.meta}
          onClose={() => setCtrlState({ open: false, variable: null, meta: null })}
          onConfirm={handleControlConfirm}
        />

        {/* ── Latest Values table ──────────────────────────────────────────── */}
        {tableVars.length > 0 && (
          <ReadingsTable
            vars={tableVars}
            latest={latest}
            getVarLabel={getVarLabel}
            formatVarValue={formatVarValue}
            writableVars={writableVars}
            plcTimezone={plcTimezone}
          />
        )}

        {/* ── Charts grid: merged groups first, then individual vars ──────── */}
        <Grid container spacing={2}>
          {/* Merged group charts — only if their card is selected, in drag order */}
          {varOrder.filter(key => key.startsWith("group-") && selectedVars.includes(key)).map((groupKey) => {
            const gi    = parseInt(groupKey.split("-")[1], 10);
            const group = variableGroups[gi];
            if (!group) return null;
            const datasets = group.variable_names.map((v, i) => ({
              label: getVarLabel(v),
              data: getDisplayHistory(v),
              tension: 0.25, pointRadius: 0, pointHoverRadius: 0,
              borderColor: colors[i % colors.length],
              borderWidth: 2,
            }));
            const hasAnyData = datasets.some(d => d.data.length > 0);

            return (
              <Grid item xs={12} key={groupKey}>
                <Paper elevation={0} sx={{
                  p: 2.25, borderRadius: "var(--card-radius)",
                  border: "1px solid var(--border-color)", backgroundColor: "var(--bg-elevated)", boxShadow: "var(--shadow-soft)",
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-main)", mb: 0.5 }}>
                        {group.name || `Group ${gi + 1}`}
                      </Typography>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        {group.variable_names.map((v, i) => (
                          <Box key={v} sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, fontSize: "0.75rem", color: "var(--text-muted)" }}>
                            <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", background: colors[i % colors.length], flexShrink: 0 }} />
                            <span>{getVarLabel(v)}</span>
                            <Typography component="span" sx={{ fontWeight: 700, color: "var(--text-main)", fontSize: "0.85rem", ml: 0.25 }}>
                              {formatVarValue(v, latest[v]?.value)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <IconButton size="small" onClick={() => setExpandedVar(groupKey)} title="Expand"
                        sx={{ color: "var(--text-muted)", "&:hover": { color: "var(--text-main)" } }}>
                        <OpenInFullIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {group.variable_names.some(v => !!getWritableMetaForDisplayVar(v)) && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      {group.variable_names.map(v => {
                        const wm = getWritableMetaForDisplayVar(v);
                        if (!wm) return null;
                        return (
                          <Button key={v} size="small" variant="outlined"
                            onClick={() => setCtrlState({ open: true, variable: wm.variable_name, meta: wm })}>
                            Set {getVarLabel(v)}
                          </Button>
                        );
                      })}
                    </Stack>
                  )}
                  <Box sx={{ height: 300 }}>
                    {hasAnyData ? (
                      <Line
                        key={`${groupKey}-${mode}-${globalHours}-${anchorMs}`}
                        options={buildChartOptions(mode, globalHours, anchorMs, true, (lbl, y) => {
                            const varName = group.variable_names.find(v => getVarLabel(v) === lbl) || lbl;
                            return formatVarValue(varName, y);
                          }, plcTimezone)}
                        data={{ datasets }}
                      />
                    ) : <NoDataBox />}
                  </Box>
                </Paper>
              </Grid>
            );
          })}

          {/* Individual variable charts — plot vars only, in card order */}
          {displayVars.filter(v => selectedVars.includes(v) && !groupedVarSet.has(v)).map((v) => {
            const writableMeta   = getWritableMetaForDisplayVar(v);
            const displayHistory = getDisplayHistory(v);
            const latestVal      = latest[v]?.value;
            const latestTs       = latest[v]?.timestamp;
            const lastPt         = displayHistory[displayHistory.length - 1];
            const displayValue   = lastPt?.y ?? latestVal ?? "—";
            const displayTs      = lastPt?.x ?? latestTs;

            return (
              <Grid item xs={12} md={6} key={v}>
                <Paper elevation={0} sx={{
                  p: 2.25, height: "100%", borderRadius: "var(--card-radius)",
                  border: "1px solid var(--border-color)", backgroundColor: "var(--bg-elevated)", boxShadow: "var(--shadow-soft)",
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                        <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", background: colors[0], flexShrink: 0 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-main)" }}>{getVarLabel(v)}</Typography>
                        {writableMeta
                          ? <Chip size="small" label={`Writable · ${writableMeta.data_type}`} color="success" variant="outlined" />
                          : <Chip size="small" label="Read only" variant="outlined" />}
                      </Stack>
                      <Typography variant="body2" sx={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{v}</Typography>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.25}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "var(--text-main)" }}>
                          {formatVarValue(v, displayValue)}
                          {writableMeta?.unit && <Box component="span" sx={{ ml: 0.5, fontSize: "0.9rem", fontWeight: 600, color: "var(--text-muted)" }}>{writableMeta.unit}</Box>}
                        </Typography>
                        <IconButton size="small" onClick={() => setExpandedVar(v)} title="Expand"
                          sx={{ color: "var(--text-muted)", "&:hover": { color: "var(--text-main)" } }}>
                          <OpenInFullIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Stack>
                      <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                        Updated {formatTimestamp(displayTs, plcTimezone)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {writableMeta && (
                        <Button size="small" variant="outlined" sx={{ borderRadius: 999 }}
                          onClick={() => setCtrlState({ open: true, variable: writableMeta.variable_name, meta: writableMeta })}>
                          Set Variable
                        </Button>
                      )}
                      {writableMeta?.unit  && <Chip size="small" label={`Unit · ${writableMeta.unit}`}  variant="outlined" />}
                      {formatRange(writableMeta) && <Chip size="small" label={`Range · ${formatRange(writableMeta)}`} variant="outlined" />}
                    </Stack>
                  </Stack>

                  <Box sx={{ height: 270 }}>
                    {displayHistory.length > 0 ? (
                      <Line
                        key={`${v}-${mode}-${globalHours}-${anchorMs}`}
                        options={buildChartOptions(mode, globalHours, anchorMs, false, (lbl, y) => formatVarValue(v, y), plcTimezone)}
                        data={{ datasets: [{ label: getVarLabel(v), data: displayHistory, tension: 0.25, pointRadius: 0, pointHoverRadius: 0, borderColor: colors[0], borderWidth: 2 }] }}
                      />
                    ) : <NoDataBox />}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

      </Box>
      </Box>

      {/* Full-screen modal */}
      {expandedVar && (
        <ExpandedModal
          varKey={expandedVar}
          label={expandedIsGroup ? (expandedGroup?.name || `Group ${expandedGroupIdx + 1}`) : getVarLabel(expandedVar)}
          displayHistory={expandedIsGroup ? [] : getDisplayHistory(expandedVar)}
          latestValue={expandedIsGroup ? "" : formatVarValue(expandedVar, latest[expandedVar]?.value)}
          latestTimestamp={expandedIsGroup ? null : latest[expandedVar]?.timestamp}
          mode={mode}
          globalHours={globalHours}
          anchorMs={anchorMs}
          onStepBack={stepBack}
          onStepForward={stepForward}
          onGoLive={goLive}
          isGroup={expandedIsGroup}
          groupDatasets={expandedGroupDatasets}
          formatYValue={expandedIsGroup
            ? (lbl, y) => { const v = expandedGroup?.variable_names.find(n => getVarLabel(n) === lbl) || lbl; return formatVarValue(v, y); }
            : (lbl, y) => formatVarValue(expandedVar, y)
          }
          plcTimezone={plcTimezone}
          onClose={() => setExpandedVar(null)}
        />
      )}

      <Footer />
    </div>
  );
}

export default PLCDetail;
